/**
 * "My sayings" on the profile page: the picker's own order, and the queue a custom one goes into.
 *
 * The player report it answers: "Then they will be able to add their own sayings which will go
 * into a queue to be approved by admins/moderators. Once approved, these are added to their list
 * which they can edit to remove the default ones or re-order, etc..."
 *
 * Three things the shapes here follow from, all of them the server's:
 *
 * - **A list is a PREFERENCE, not a permission.** What a player may *say* is the catalogue plus
 *   their approved submissions; what this edits is which of those their picker offers and in what
 *   order (`user_saying_list`). Removing "Good game!" changes the picker and not what the server
 *   accepts — which is exactly why a removed default can be put back from here with no round trip
 *   to ask what it was: `DEFAULT_SAYINGS` is a shared constant, and the approved half is already
 *   on `submissions`.
 * - **One mutation replaces the whole list**, because "remove" and "re-order" are the same edit to
 *   a list. So this screen holds a draft and saves it in one go, rather than firing a mutation per
 *   arrow press — and that is what lets a *drag* be free: it is a hundred edits to a draft nobody
 *   is sending anywhere, not a hundred requests.
 * - **An empty list is refused** (`MIN_SAYING_LIST_LENGTH`), because "no rows" already means
 *   "never edited" and reads as the whole vocabulary. The last entry therefore cannot be removed
 *   here either — the browser refuses exactly what the API refuses, which is the rule the length
 *   bounds on the submission box are under too.
 *
 * **There are four ways to move a row and they are all the same edit**, `moveSayingTo` in
 * `saying-order.ts`. The card began with only the arrows, and the report that grew the other three
 * is what they cost: *"the order controls are too slow! If I add a custom saying and want to put
 * it at the top, it takes forever pushing the arrow button for each row."*
 *
 * - **The arrows stay, and they are still the accessible floor.** Keyboard-operable and announced
 *   by a screen reader with no pointer choreography at all; a drag is additive and may not be the
 *   only way to do anything.
 * - **The position field is the drag's accessible equal**, and it is the control the report
 *   actually asked for: typing `1` into a row is "send it to the top" in one action, from a
 *   keyboard, with no dragging and no forty presses.
 * - **The drag is hand-rolled pointer events**, ~30 lines against a dependency in every bundle
 *   this client ships. The geometry is `dropIndexAt` and the move is `moveSayingTo`, both pure and
 *   both tested, so what is left here is three handlers and a pointer capture.
 * - **Restore default order** re-sorts the draft into the catalogue's order. It reorders and
 *   re-adds nothing — see `restoreDefaultSayingOrder` for why.
 */

import {
  DEFAULT_SAYINGS,
  MAX_SAYING_LIST_LENGTH,
  MIN_SAYING_LIST_LENGTH,
  SAYING_TEXT_MAX_LENGTH,
  SAYING_TEXT_MIN_LENGTH,
} from '@chompdome/shared/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import {
  dropIndexAt,
  moveSayingTo,
  parseSayingPosition,
  restoreDefaultSayingOrder,
} from './saying-order';

/** One saying as `sayings.list` reports it. */
interface ListedSaying {
  id: string;
  text: string;
  custom: boolean;
}

/** One submission as `sayings.list` reports it. */
interface Submission {
  id: string;
  text: string;
  status: string;
  createdAt: string;
  reviewedAt: string | null;
}

export function SayingsCard() {
  const utils = trpc.useUtils();
  const mine = trpc.sayings.list.useQuery();

  const list = mine.data?.list ?? [];
  const submissions = mine.data?.submissions ?? [];

  /**
   * The order being edited. Seeded from the server's answer and re-seeded whenever that changes —
   * a save, or an approval landing — so the draft is never a stale copy of a list somebody else's
   * verdict has changed under it.
   */
  const [draft, setDraft] = useState<string[]>([]);
  const savedKey = list.map((row) => row.id).join(',');
  useEffect(() => {
    setDraft(savedKey === '' ? [] : savedKey.split(','));
  }, [savedKey]);

  const updateList = trpc.sayings.updateList.useMutation({
    onSuccess: async () => {
      await utils.sayings.list.invalidate();
    },
  });

  /**
   * Everything this account may say, whether or not it is on their list.
   *
   * Assembled here rather than fetched: the catalogue is a shared constant and the approved half
   * is already on `submissions`, so "put that default back" costs no procedure of its own. What it
   * must not do is *widen* anything — a saying that is not in this set is one `updateList` would
   * refuse, and it says so.
   */
  const approved = submissions.filter((row) => row.status === 'approved');
  const vocabulary: ListedSaying[] = [
    ...DEFAULT_SAYINGS.map((saying) => ({ id: saying.id, text: saying.text, custom: false })),
    ...approved.map((row) => ({ id: row.id, text: row.text, custom: true })),
  ];
  const textOf = new Map(vocabulary.map((row) => [row.id, row]));
  const missing = vocabulary.filter((row) => !draft.includes(row.id));
  const dirty = draft.join(',') !== savedKey;

  /** The one edit all four controls make. */
  const moveTo = useCallback((from: number, to: number) => {
    setDraft((current) => {
      const next = moveSayingTo(current, from, to);
      return next === current ? current : [...next];
    });
  }, []);

  const customOrder = approved.map((row) => row.id);
  const ordered = restoreDefaultSayingOrder(draft, customOrder);

  /**
   * Where each row is on screen, so a drag can ask which one the pointer is over.
   *
   * A ref rather than state: it is read on a pointer move and never rendered from, and a rect that
   * re-rendered the list on every frame of a drag would be re-measuring what it just moved.
   */
  const rowElements = useRef(new Map<string, HTMLLIElement>());
  const [draggingId, setDraggingId] = useState<string | null>(null);

  /**
   * The drag is a *live* reorder: the row under the pointer swaps as you pass it, so what is on
   * screen mid-drag is already the answer and the release commits nothing of its own. That is what
   * lets the whole gesture be three handlers — and it is only affordable because the draft is not
   * a mutation (see the header).
   */
  const dragTo = useCallback((id: string, pointerY: number) => {
    setDraft((current) => {
      const from = current.indexOf(id);
      if (from < 0) return current;
      const spans = current.map((rowId) => {
        const rect = rowElements.current.get(rowId)?.getBoundingClientRect();
        return { top: rect?.top ?? 0, height: rect?.height ?? 0 };
      });
      const next = moveSayingTo(current, from, dropIndexAt(spans, pointerY));
      return next === current ? current : [...next];
    });
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>My sayings</CardTitle>
        <CardDescription>
          What the Say button in the arena offers, in the order you put it. Removing one here does
          not stop you saying it — it just takes it off your picker.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {mine.error && (
          <p className="text-sm text-destructive" role="alert">
            {mine.error.message}
          </p>
        )}

        <section className="space-y-2">
          {mine.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Drag a row by its handle, or type the place you want it in and press Enter. The
                arrows do one step at a time.
              </p>
              <ol data-testid="saying-list" className="space-y-1">
                {draft.map((id, index) => (
                  <ListRow
                    key={id}
                    saying={textOf.get(id) ?? { id, text: id, custom: true }}
                    index={index}
                    total={draft.length}
                    dragging={draggingId === id}
                    register={(element) => {
                      if (element) rowElements.current.set(id, element);
                      else rowElements.current.delete(id);
                    }}
                    onMoveTo={(to) => moveTo(index, to)}
                    onDragStart={() => setDraggingId(id)}
                    onDragMove={(pointerY) => dragTo(id, pointerY)}
                    onDragEnd={() => setDraggingId(null)}
                    onRemove={() => setDraft(draft.filter((row) => row !== id))}
                  />
                ))}
              </ol>
            </>
          )}

          {updateList.error && (
            <p className="text-sm text-destructive" role="alert">
              {updateList.error.message}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              size="sm"
              data-testid="save-saying-list"
              disabled={!dirty || updateList.isPending || draft.length < MIN_SAYING_LIST_LENGTH}
              onClick={() => updateList.mutate({ sayingIds: draft })}
            >
              {updateList.isPending ? 'Saving…' : 'Save order'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="restore-saying-order"
              // Nothing to restore when the draft is already in catalogue order — the button says
              // "order" and means it, so a list that is in that order has nothing for it to do.
              disabled={ordered === draft}
              onClick={() => setDraft([...ordered])}
            >
              Restore default order
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDraft(savedKey === '' ? [] : savedKey.split(','))}
              >
                Undo changes
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              {draft.length} of {MAX_SAYING_LIST_LENGTH}
            </span>
          </div>
        </section>

        {missing.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Not on your list
            </h3>
            <ul data-testid="saying-missing" className="flex flex-wrap gap-1.5">
              {missing.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    data-testid={`add-saying-${row.id}`}
                    className="rounded-full border border-border/60 px-2.5 py-1 text-xs transition-colors hover:bg-secondary/60"
                    onClick={() => setDraft([...draft, row.id])}
                  >
                    + {row.text}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <SubmitForm
          pending={submissions.filter((row) => row.status === 'pending').length}
          maxPending={mine.data?.limits.maxPending ?? 0}
        />

        {submissions.length > 0 && <SubmissionList rows={submissions} />}
      </CardContent>
    </Card>
  );
}

function ListRow({
  saying,
  index,
  total,
  dragging,
  register,
  onMoveTo,
  onDragStart,
  onDragMove,
  onDragEnd,
  onRemove,
}: {
  saying: ListedSaying;
  index: number;
  total: number;
  dragging: boolean;
  register: (element: HTMLLIElement | null) => void;
  onMoveTo: (to: number) => void;
  onDragStart: () => void;
  onDragMove: (pointerY: number) => void;
  onDragEnd: () => void;
  onRemove: () => void;
}) {
  return (
    <li
      ref={register}
      data-testid={`saying-row-${saying.id}`}
      data-dragging={dragging ? 'true' : undefined}
      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
        dragging ? 'border-primary/60 bg-secondary/40 shadow-sm' : 'border-border/50'
      }`}
    >
      {/* The handle is the only part of the row that drags, and it is deliberately not a control:
          a button that does nothing when pressed with a keyboard is a trap, and the two things a
          keyboard needs instead — the position field and the arrows — are right beside it. */}
      <span
        aria-hidden="true"
        title="Drag to reorder"
        data-testid={`drag-saying-${saying.id}`}
        // `touch-none` is what stops a thumb dragging the page instead of the row.
        className="shrink-0 cursor-grab touch-none select-none px-0.5 text-sm leading-none text-muted-foreground active:cursor-grabbing"
        onPointerDown={(event) => {
          event.preventDefault();
          // Capture is what keeps the moves coming to this handle once the pointer has left it —
          // the whole reason no window listener is needed. jsdom has no pointer capture at all.
          event.currentTarget.setPointerCapture?.(event.pointerId);
          onDragStart();
        }}
        onPointerMove={(event) => {
          if (dragging) onDragMove(event.clientY);
        }}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        ⠿
      </span>
      <PositionField
        text={saying.text}
        id={saying.id}
        index={index}
        total={total}
        onMoveTo={onMoveTo}
      />
      <span data-testid="saying-text" className="min-w-0 flex-1 truncate text-sm">
        {saying.text}
      </span>
      {saying.custom && (
        <span className="shrink-0 rounded-full border border-primary/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
          Yours
        </span>
      )}
      {/* One step at a time, and the accessible floor the drag is additive to: operable from a
          keyboard and announced by a screen reader with no pointer choreography at all. */}
      <button
        type="button"
        aria-label={`Move “${saying.text}” up`}
        data-testid={`move-up-${saying.id}`}
        disabled={index === 0}
        onClick={() => onMoveTo(index - 1)}
        className="rounded px-1.5 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
      >
        ↑
      </button>
      <button
        type="button"
        aria-label={`Move “${saying.text}” down`}
        data-testid={`move-down-${saying.id}`}
        disabled={index === total - 1}
        onClick={() => onMoveTo(index + 1)}
        className="rounded px-1.5 py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
      >
        ↓
      </button>
      <button
        type="button"
        aria-label={`Remove “${saying.text}” from your list`}
        data-testid={`remove-saying-${saying.id}`}
        // The floor is the API's: a list of nothing is indistinguishable from a list nobody has
        // edited, so the last row cannot be taken away here either.
        disabled={total <= MIN_SAYING_LIST_LENGTH}
        onClick={onRemove}
        className="rounded px-1.5 py-0.5 text-sm text-muted-foreground transition-colors hover:text-destructive disabled:opacity-30"
      >
        ✕
      </button>
    </li>
  );
}

/**
 * The row's place in the list, as a number somebody can type over.
 *
 * It holds its own text while it is being typed into and commits on Enter or on blur, because a
 * field that moved the row on every keystroke would send it to row 1 on the way to typing 12. The
 * committed value is `parseSayingPosition`'s: out of range is clamped rather than refused, and
 * anything that is not a whole number puts the row's real place back.
 *
 * It re-seeds itself whenever the row's real place changes — after a commit that moved it, after
 * a drag, after an arrow press — rather than being re-mounted for it, so the field keeps focus and
 * a second `1` + Enter is two keys rather than a click and two keys.
 */
function PositionField({
  id,
  text,
  index,
  total,
  onMoveTo,
}: {
  id: string;
  text: string;
  index: number;
  total: number;
  onMoveTo: (to: number) => void;
}) {
  const [value, setValue] = useState(String(index + 1));
  useEffect(() => {
    setValue(String(index + 1));
  }, [index]);

  const commit = () => {
    const to = parseSayingPosition(value, total);
    if (to === null || to === index) {
      setValue(String(index + 1));
      return;
    }
    onMoveTo(to);
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      aria-label={`Place of “${text}” in your list, 1 to ${total}`}
      data-testid={`position-saying-${id}`}
      className="h-7 w-10 shrink-0 px-1 text-center text-xs"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      }}
    />
  );
}

/**
 * Send one saying to the moderation queue.
 *
 * The bounds are the shared ones, so the browser refuses exactly what the API refuses — and the
 * two policy refusals it cannot know in advance (the pending cap, the duplicate) come back as
 * sentences from the server and are printed as they are.
 */
function SubmitForm({ pending, maxPending }: { pending: number; maxPending: number }) {
  const utils = trpc.useUtils();
  const [text, setText] = useState('');

  const submit = trpc.sayings.submit.useMutation({
    onSuccess: async () => {
      setText('');
      await utils.sayings.list.invalidate();
    },
  });

  const trimmed = text.trim();
  const tooShort = trimmed.length < SAYING_TEXT_MIN_LENGTH;

  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Send one of your own
      </h3>
      <p className="text-xs text-muted-foreground">
        A moderator reads it before anybody hears it. {pending} of {maxPending} waiting.
      </p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!tooShort) submit.mutate({ text: trimmed });
        }}
      >
        <Input
          aria-label="A saying to send for approval"
          placeholder="Something you would say mid-chase"
          className="max-w-80"
          value={text}
          maxLength={SAYING_TEXT_MAX_LENGTH}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          type="submit"
          size="sm"
          data-testid="submit-saying"
          disabled={tooShort || submit.isPending}
        >
          {submit.isPending ? 'Sending…' : 'Send for approval'}
        </Button>
      </form>
      {submit.error && (
        <p className="text-sm text-destructive" role="alert">
          {submit.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * Everything this account has sent, newest first — the rejected ones included.
 *
 * `user_sayings` is append-only, so a verdict is a status on the row that was already there. A
 * queue that could not answer "what happened to the one I sent" is a queue people re-submit into,
 * which is the whole reason a rejection is kept and shown.
 */
function SubmissionList({ rows }: { rows: readonly Submission[] }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        What you have sent
      </h3>
      <ul data-testid="saying-submissions" className="space-y-1">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center gap-2 rounded-md border border-border/50 px-2 py-1.5"
          >
            <span className="min-w-0 flex-1 truncate text-sm">{row.text}</span>
            <StatusBadge status={row.status} />
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {new Date(row.createdAt).toLocaleDateString()}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const STATUS_CLASS: Record<string, string> = {
  pending: 'border-amber-400/50 text-amber-300',
  approved: 'border-primary/50 text-primary',
  rejected: 'border-destructive/50 text-destructive',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      data-testid={`saying-status-${status}`}
      className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
        STATUS_CLASS[status] ?? 'border-border/60 text-muted-foreground'
      }`}
    >
      {status}
    </span>
  );
}
